import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ArrowDownToLine, ArrowUpFromLine, PackageSearch, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtDate, today } from "@/lib/format";
import { PageHero } from "@/components/premium/UI";

const CATEGORIES = ["Feed", "Vaccines", "Medicine", "Building Materials", "Tools", "Equipment", "Household Supplies", "Fuel", "Water", "Consumables"];

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [dash, setDash] = useState(null);
  async function refresh() {
    try {
      const [i, d] = await Promise.all([api.get("/inventory/items"), api.get("/inventory/dashboard")]);
      setItems(i.data); setDash(d.data);
    } catch { toast.error("Could not load Inventory360"); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <PageHero
        eyebrow="Inventory360"
        title="Stock & consumables"
        subtitle="Stock levels, movements, and low-stock alerts."
        moduleKind="inventory"
      />

      {dash && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="kpi-card" data-testid="inv-kpi-total">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Total items</div>
            <div className="mt-2 font-heading text-3xl font-semibold">{dash.total_items}</div>
          </div>
          <div className="kpi-card" data-testid="inv-kpi-low">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Low stock</div>
            <div className="mt-2 font-heading text-3xl font-semibold text-destructive">{dash.low_stock_count}</div>
          </div>
          <div className="kpi-card">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Categories</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(dash.by_category || []).map((c) => <Badge key={c.category} variant="outline">{c.category} · {c.count}</Badge>)}
            </div>
          </div>
        </div>
      )}

      <Card className="rounded-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div><CardTitle>Items</CardTitle><CardDescription>{items.length} tracked</CardDescription></div>
          <div className="flex gap-2">
            <NewItem onCreated={refresh} />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Unit</TableHead>
              <TableHead>Balance</TableHead><TableHead>Minimum</TableHead><TableHead>Supplier</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id} data-testid={`inv-row-${i.name.replace(/\s+/g,'-').toLowerCase()}`}>
                  <TableCell className="font-medium flex items-center gap-2">
                    {i.name}
                    {Number(i.current_balance) <= Number(i.minimum_level) && Number(i.minimum_level) > 0 && <AlertTriangle className="h-3 w-3 text-destructive" />}
                  </TableCell>
                  <TableCell>{i.category}</TableCell>
                  <TableCell>{i.unit}</TableCell>
                  <TableCell>{fmt(i.current_balance, 1)}</TableCell>
                  <TableCell>{fmt(i.minimum_level, 1)}</TableCell>
                  <TableCell>{i.supplier}</TableCell>
                  <TableCell className="text-right">
                    <MovementBtn item={i} type="in" onDone={refresh} />
                    <MovementBtn item={i} type="out" onDone={refresh} />
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center subtle py-8">No items yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function NewItem({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Feed", unit: "kg", current_balance: 0, minimum_level: 0, supplier: "" });
  async function submit() {
    try { await api.post("/inventory/items", { ...form, current_balance: Number(form.current_balance), minimum_level: Number(form.minimum_level) }); toast.success("Item added"); setOpen(false); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-inv-btn"><Plus className="h-4 w-4 mr-1" />New item</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New inventory item</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
          <div><Label>Current balance</Label><Input type="number" value={form.current_balance} onChange={(e) => setForm({ ...form, current_balance: e.target.value })} /></div>
          <div><Label>Minimum level</Label><Input type="number" value={form.minimum_level} onChange={(e) => setForm({ ...form, minimum_level: e.target.value })} /></div>
          <div className="col-span-2"><Label>Supplier</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MovementBtn({ item, type, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ quantity: 1, unit_cost: 0, notes: "" });
  async function submit() {
    try {
      await api.post("/inventory/movements", { item_id: item.id, date: today(), type, quantity: Number(form.quantity), unit_cost: Number(form.unit_cost), notes: form.notes });
      toast.success(`Stock ${type === "in" ? "added" : "removed"}`); setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`inv-${type}-${item.name.replace(/\s+/g,'-').toLowerCase()}`}>
          {type === "in" ? <ArrowDownToLine className="h-3.5 w-3.5" /> : <ArrowUpFromLine className="h-3.5 w-3.5" />}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{type === "in" ? "Add stock" : "Consume stock"} — {item.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Quantity ({item.unit})</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
          {type === "in" && <div><Label>Unit cost</Label><Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></div>}
          <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
